/**
 * site-config.ts — Typed commercial constants (single source of truth).
 *
 * All numbers and strings below come from the approved master spec (4 Aug 2026)
 * and are FINAL. Homepage pricing preview, /pricing, signup, metadata and
 * JSON-LD will all import from this file — never duplicate these values.
 */

// ─────────────────────────────────────────────────────────────────────────

export interface Plan {
  id: string
  label: string
  bestFor: string
  monthlyPrice: number | null
  annualPrice: number | null
  description: string
  brands: string
  promptsPerEngine: string
  surfaces: string
  promptRuns: string
  citationCollection: string
  actionsPerMonth: string | number
  proposalsPerAction: number
  contentPiecesPerMonth: string | number
  aeoScansPerMonth: string | number
  creditsPerMonth: string | number
  claudeAddon: string | null
  cta: string
}

export const PLANS = [
  {
    id: 'starter',
    label: 'STARTER',
    bestFor: 'Founders and smaller teams',
    monthlyPrice: 99,
    annualPrice: 990,
    description: 'A complete AI-search loop for one brand.',
    brands: '1',
    promptsPerEngine: '50',
    surfaces: 'ChatGPT, Gemini',
    promptRuns: 'Daily',
    citationCollection: 'Daily',
    actionsPerMonth: 15,
    proposalsPerAction: 3,
    contentPiecesPerMonth: 30,
    aeoScansPerMonth: 1,
    creditsPerMonth: 400,
    claudeAddon: null,
    cta: 'Start free',
  },
  {
    id: 'pro',
    label: 'PRO · CORE',
    bestFor: 'Growth marketing teams',
    monthlyPrice: 349,
    annualPrice: 3490,
    description: 'Broader answer-surface coverage and more execution capacity for one brand.',
    brands: '1',
    promptsPerEngine: '100',
    surfaces: 'ChatGPT, Gemini, Google AI Mode, Google AI Overviews, Perplexity',
    promptRuns: 'Daily',
    citationCollection: 'Daily',
    actionsPerMonth: 20,
    proposalsPerAction: 3,
    contentPiecesPerMonth: 60,
    aeoScansPerMonth: 2,
    creditsPerMonth: 1000,
    claudeAddon: 79,
    cta: 'Start free',
  },
  {
    id: 'scale',
    label: 'SCALE · RECOMMENDED FOR MULTI-BRAND TEAMS',
    bestFor: 'Multi-brand teams and agencies',
    monthlyPrice: 899,
    annualPrice: 8990,
    description: 'Run the full loop across a portfolio from one workspace.',
    brands: 'Up to 3',
    promptsPerEngine: '300 across the workspace',
    surfaces: 'ChatGPT, Gemini, Google AI Mode, Google AI Overviews, Perplexity',
    promptRuns: 'Daily',
    citationCollection: 'Daily',
    actionsPerMonth: 60,
    proposalsPerAction: 3,
    contentPiecesPerMonth: 200,
    aeoScansPerMonth: 4,
    creditsPerMonth: 5000,
    claudeAddon: 199,
    cta: 'Start free',
  },
  {
    id: 'enterprise',
    label: 'ENTERPRISE',
    bestFor: 'Large brands and agency networks',
    monthlyPrice: null,
    annualPrice: null,
    description: 'Configured coverage, capacity and integrations for complex organizations.',
    brands: '3+',
    promptsPerEngine: '200–500 configured',
    surfaces: 'All engines including Claude, configured',
    promptRuns: 'Daily',
    citationCollection: 'Daily',
    actionsPerMonth: 'Custom',
    proposalsPerAction: 3,
    contentPiecesPerMonth: 400,
    aeoScansPerMonth: 8,
    creditsPerMonth: 12000,
    claudeAddon: 'included',
    cta: 'Talk to us',
  },
] as const

export const ANNUAL_NOTE = 'Annual plans are billed upfront at the price of ten months.'

// ─────────────────────────────────────────────────────────────────────────

export interface CreditCost {
  name: string
  credits: number
}

export const CREDIT_COSTS: CreditCost[] = [
  {
    name: 'Extra Action, including 3 proposals',
    credits: 100,
  },
  {
    name: 'Extra proposal',
    credits: 30,
  },
  {
    name: 'Extra content piece',
    credits: 30,
  },
  {
    name: 'Full on-demand refresh across all prompts and included engines, with citations',
    credits: 300,
  },
  {
    name: 'Extra AEO scan',
    credits: 300,
  },
]

export interface TopUpPack {
  credits: number
  price: number
}

export const TOPUP_PACKS: TopUpPack[] = [
  { credits: 500, price: 10 },
  { credits: 1500, price: 25 },
  { credits: 3500, price: 50 },
  { credits: 20000, price: 250 },
]

export const TOPUP_COPY = 'Add credits without changing plans. Purchased credits enter the same workspace pool for on-demand work.'

// ─────────────────────────────────────────────────────────────────────────

export interface Surface {
  name: string
}

export const SURFACES = [
  { name: 'ChatGPT' },
  { name: 'Gemini' },
  { name: 'Google AI Mode' },
  { name: 'Google AI Overviews' },
  { name: 'Perplexity' },
  { name: 'Claude' },
] as const

export const SURFACES_LABEL = 'Coverage across leading AI answer surfaces'

// ─────────────────────────────────────────────────────────────────────────

export interface DemoWorkspace {
  brand: string
  buyerQuestion: string
  position: string
  firstChoice: string
  competitorsAhead: number
  citedSources: number
  openActions: number
  frameLabel: string
}

export const DEMO_WORKSPACE: DemoWorkspace = {
  brand: 'Northstar',
  buyerQuestion: 'Which customer data platform is best for a multi-brand retailer?',
  position: '3 of 5',
  firstChoice: '1 of 5 surfaces',
  competitorsAhead: 2,
  citedSources: 12,
  openActions: 4,
  frameLabel: 'Product view · Demo workspace',
}

// ─────────────────────────────────────────────────────────────────────────

export interface SiteMeta {
  metaTitle: string
  metaDescription: string
  footerPositioning: string
  copyright: string
}

export const SITE_META: SiteMeta = {
  metaTitle: 'Velova | AI Visibility Intelligence Platform',
  metaDescription: 'See how your brand appears across leading AI answer surfaces, understand why competitors win, and run the content, source and technical work that changes what buyers see.',
  footerPositioning: 'Velova turns AI answers into an operating channel: measure the buyer question, find the gap, run the work and prove what changed.',
  copyright: '© 2026 Velova.',
}

// ─────────────────────────────────────────────────────────────────────────

export type AnalyticsEventName =
  | 'cta_start_free'
  | 'cta_see_product'
  | 'verdict_started'
  | 'verdict_completed'
  | 'verdict_track_clicked'
  | 'pricing_plan_selected'
  | 'contact_submitted'
  | 'signup_started'
  | 'signup_completed'

export const ANALYTICS_EVENTS = [
  'cta_start_free',
  'cta_see_product',
  'verdict_started',
  'verdict_completed',
  'verdict_track_clicked',
  'pricing_plan_selected',
  'contact_submitted',
  'signup_started',
  'signup_completed',
] as const

// ─────────────────────────────────────────────────────────────────────────

export interface JsonLdOffer {
  '@type': string
  priceCurrency: string
  price: string
  name: string
}

export interface JsonLdSoftwareApplication {
  '@context': string
  '@type': string
  name: string
  applicationCategory: string
  offers: JsonLdOffer[]
}

/**
 * Build JSON-LD for SoftwareApplication with Starter, Pro, Scale pricing.
 * Enterprise has no numeric price (shown as "Contact for pricing").
 */
export function buildJsonLd(): JsonLdSoftwareApplication {
  const offers: JsonLdOffer[] = PLANS.slice(0, 3).map((plan) => ({
    '@type': 'Offer',
    priceCurrency: 'USD',
    price: String(plan.monthlyPrice),
    name: plan.label,
  }))

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Velova',
    applicationCategory: 'BusinessApplication',
    offers,
  }
}
